import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Description,
} from '@mui/icons-material';
import { uploadFile } from '../utils/api';

interface FileUploadProps {
  accept: string;
  endpoint: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
  onUploadSuccess?: (response: any) => void;
  onUploadError?: (error: string) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  accept,
  endpoint,
  title,
  description,
  icon = <Description />,
  onUploadSuccess,
  onUploadError,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [uploadedFile, setUploadedFile] = useState<string>('');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploading(true);
    setUploadStatus('idle');
    setMessage('');

    try {
      const response = await uploadFile(endpoint, file);

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      setUploadStatus('success');
      setMessage(result.message);
      setUploadedFile(file.name);
      onUploadSuccess?.(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setUploadStatus('error');
      setMessage(errorMessage);
      onUploadError?.(errorMessage);
    } finally {
      setUploading(false);
    }
  }, [endpoint, onUploadSuccess, onUploadError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { [accept]: [] },
    multiple: false,
    disabled: uploading,
  });

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        {title}
      </Typography>
      
      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          bgcolor: isDragActive ? 'primary.light' : 'grey.50',
          cursor: uploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: uploading ? 'grey.300' : 'primary.main',
            bgcolor: uploading ? 'grey.50' : 'primary.light',
          },
        }}
      >
        <input {...getInputProps()} />
        
        <Box sx={{ textAlign: 'center' }}>
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          
          <Typography variant="body1" sx={{ mb: 1 }}>
            {isDragActive
              ? `Drop ${description.toLowerCase()} here...`
              : `Drag and drop ${description.toLowerCase()} here, or click to select`
            }
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>

          {uploadedFile && (
            <Chip
              label={`Uploaded: ${uploadedFile}`}
              color="success"
              variant="outlined"
              sx={{ mt: 2 }}
            />
          )}
        </Box>

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Uploading...
            </Typography>
          </Box>
        )}
      </Paper>

      {uploadStatus === 'success' && (
        <Alert
          icon={<CheckCircle fontSize="inherit" />}
          severity="success"
          sx={{ mt: 2 }}
        >
          {message}
        </Alert>
      )}

      {uploadStatus === 'error' && (
        <Alert
          icon={<ErrorIcon fontSize="inherit" />}
          severity="error"
          sx={{ mt: 2 }}
        >
          {message}
        </Alert>
      )}
    </Box>
  );
};

export default FileUpload;